import React, { useState, useEffect, useMemo } from "react";
import {
  Button,
  Input,
  Card,
  CardBody,
  CardHeader,
  FormGroup,
  Label,
  Form,
  Row,
  Col,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Table,
  Alert,
} from "reactstrap";
// Adicionando os imports necessários para a API
import { api } from "../../config";
import { fetchComAuth, fetchComAuthJson } from '../../common/api';

const TelaCategoria = ({ feiraId, editando }) => {
  // O estado agora começa vazio e será preenchido pela API
  const [categorias, setCategorias] = useState([]);

  // Estados do Modal
  const [modalAberto, setModalAberto] = useState(false);
  const [editandoId, setEditandoId] = useState(null);

  // Campos do Formulário
  const [nome, setNome] = useState("");
  const [descricao, setDescricao] = useState("");
  const [erro, setErro] = useState("");

  // Função para carregar os dados da API
  const carregarDados = async () => {
    if (!feiraId) return;
    try {
      const response = await fetchComAuthJson(`${api.API_URL}/categoria/${feiraId}`, {
        method: 'GET'
      });
      const data = await response.json();
      setCategorias(data.data || []); // Garante que seja um array
      
    } catch (err) {
      console.error("Erro ao carregar as áreas de interesse:", err);
      setErro("Não foi possível carregar os dados.");
    }
  };

  // useEffect para buscar os dados quando o componente for montado
  useEffect(() => {
    if (editando) {
      carregarDados();
    }
  }, [feiraId, editando]);

  const limparFormulario = () => {
    setEditandoId(null);
    setNome("");
    setDescricao("");
    setErro("");
  };

  const abrirNovo = () => {
    limparFormulario();
    setModalAberto(true);
  };

  const abrirEdicao = (s) => {
    setEditandoId(s.id);
    setNome(s.nome);
    setDescricao(s.descricao || "");
    setErro("");
    setModalAberto(true);
  };

  const excluir = (id) => {
    if (!window.confirm("Tem certeza que deseja excluir esta categoria?")) return;
    
    fetchComAuthJson(`${api.API_URL}/categoria/${id}`, { method: 'DELETE' })
      .then(res => {
        if (!res.ok) throw new Error('Erro ao excluir');
        // Após excluir, recarrega a lista para atualizar a tela
        carregarDados();
      })
      .catch(err => {
        console.error('Erro ao excluir categoria:', err);
        setErro("Falha ao excluir o item.");
      });
  };

  const validar = () => {
    if (!nome.trim()) return "O nome da categoria é obrigatório.";
    return "";
  };

  const handleSalvar = async () => {
    const erroValidacao = validar();
    if (erroValidacao) {
      setErro(erroValidacao);
      return;
    }

    const payload = {
      id: editandoId, // O backend usará isso para saber se é um update
      nome: nome.trim(),
      descricao: descricao.trim(),
      feira_id: feiraId
    };

    const method = editandoId ? 'PUT' : 'POST';
    const endpoint = `${api.API_URL}/categoria`;

    fetchComAuthJson(endpoint, {
      method: method,
      body: JSON.stringify(payload)
    })
    .then(res => {
      if (!res.ok) throw new Error('Erro ao salvar');
      return res.json();
    })
    .then(() => {
      // Após salvar, recarrega os dados, fecha o modal e limpa o formulário
      carregarDados();
      setModalAberto(false);
      limparFormulario();
    })
    .catch(err => {
      console.error('Erro ao salvar categoria:', err);
      setErro("Não foi possível salvar os dados. Verifique se já não existe um item com este nome.");
    });
  };

  return (
    <div>
      <Row>
        <Col xl={12}>
          <Card>
            <CardHeader className="d-flex justify-content-between align-items-center">
              <Button color="success" onClick={abrirNovo}>
                <i className="ri-add-line align-bottom me-1"></i> Adicionar Categoria
              </Button>
            </CardHeader>
            <CardBody>
              {categorias.length === 0 ? (
                <p className="text-muted">Nenhuma categoria cadastrada.</p>
              ) : (
                <div className="table-responsive">
                  <Table className="table align-middle table-nowrap">
                    <thead className="table-light">
                      <tr>
                        <th>Nome</th>
                        <th>Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {categorias.map((s) => (
                        <tr key={s.id}>
                          <td>{s.nome}</td>
                          <td>
                            <Button color="warning" size="sm" className="me-2" onClick={() => abrirEdicao(s)}>
                              Editar
                            </Button>
                            <Button color="danger" size="sm" onClick={() => excluir(s.id)}>
                              Excluir
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Modal */}
      <Modal isOpen={modalAberto} toggle={() => setModalAberto(!modalAberto)} centered>
        <ModalHeader toggle={() => setModalAberto(!modalAberto)}>
          {editandoId ? "Editar Categoria" : "Nova Categoria"}
        </ModalHeader>
        <ModalBody>
          <Form>
            {erro && <Alert color="danger">{erro}</Alert>}

            <FormGroup>
              <Label>Nome*</Label>
              <Input
                type="text"
                value={nome}
                onChange={(e) => setNome(e.target.value)}
                placeholder="Ex: Tecnologia"
                required
              />
            </FormGroup>

            <FormGroup>
              <Label>Descrição</Label>
              <Input
                type="textarea"
                rows={3}
                value={descricao}
                onChange={(e) => setDescricao(e.target.value)}
                placeholder="Texto descritivo sobre a categoria..."
              />
            </FormGroup>

            <small className="text-muted">* Campos obrigatórios</small>
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setModalAberto(false)}>
            Cancelar
          </Button>
          <Button color="primary" onClick={handleSalvar}>
            {editandoId ? "Atualizar" : "Salvar"}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}

export default TelaCategoria;